import { useQuery } from '@tanstack/react-query';
import { EyeOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { interviewQuery, isReadable, type InterviewMetadataOut } from '@/api/interviews';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import NotFound from '@/pages/NotFound';
import { onDateTime } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

import { FillingIn } from './FillingIn';
import { INTERVIEW_ID_PARAM } from './interviewUrls';
import { Submitted } from './Submitted';

/**
 * `DESIGN.md` §6.6 (filling in) and §6.7 (submitted) — **one address, three answers**, and the third
 * of them is a state `DESIGN.md` did not have until TASK-080 designed it (see *"A blinded interview,
 * and what it may say"* in §6.6).
 *
 * | The payload | What renders | Whose task |
 * |---|---|---|
 * | readable, `submitted_at === null`, **and the reader is the interviewer** | §6.6's form (`FillingIn`) | TASK-082 |
 * | readable, `submitted_at` set | §6.7's read view | TASK-085 |
 * | **blinded** — `InterviewMetadataOut` | the blinded state below | TASK-080 |
 *
 * ⚠️ **The branch is the payload's shape and `submitted_at`, and nothing else** — never a flag, never
 * a status code, never a group. `GET /api/v1/interviews/{id}/` answers **200 for all three groups**
 * and the body is what differs (ADR 0006): a colleague's draft comes back blinded to everybody,
 * `Admin` included, and a colleague's *submitted* interview comes back blinded for as long as
 * this reader owes feedback of their own for the same person. `isReadable` is the one discriminator
 * in the client (`api/interviews.ts`) and this screen adds no second one — no cast, no `'scores' in`,
 * no re-derivation of who may read what.
 *
 * ⚠️ **The author check is a second question and not the branch.** A readable draft can only ever be
 * the reader's own — ADR 0006 hides everybody else's behind the metadata shape — so
 * `interviewer_id === session.user.id` cannot change what a *correct* payload renders. It is checked
 * anyway, and the reason is what it does with an **impossible** one: a full draft body for a reader
 * who is not its author would otherwise open §6.6's form over somebody else's unsubmitted feedback,
 * which is the one thing this surface must not do however it arrived (a backend regression, a proxy
 * cache, a devtools replay). The read view is the safe answer to it, and
 * `interview.test.tsx` drives exactly that payload.
 *
 * ⚠️ **A 404 is §8's Forbidden state, not the Error state.** `/api/v1/interviews/\d+/` is a
 * **record** on the candidate surface (`CANDIDATE_SCOPED_RECORD`, `api/http.ts`), so the API answers
 * 404 — never 403 — for an interview outside the reader's reach (backend TASK-029), and the mapping
 * that already exists turns it into the Forbidden state. What this screen passes is the **sentence**:
 * it is about the *conversation*, because that is what the reader named, and the shared copy names a
 * candidate this payload cannot even identify (there is no `person_id` on the blinded shape).
 */
export default function Interview() {
  const interviewId = useParams()[INTERVIEW_ID_PARAM];

  /**
   * ⚠️ **A hand-typed `/interviews/ada` makes no request at all**, and there are two reasons rather
   * than one — §6.9's chooser validates its vacancy id in the same shape and for the first of them.
   *
   *  * `:interviewId` matches any segment, so `/api/v1/interviews/NaN/` would go out, answer 404,
   *    **miss** `CANDIDATE_SCOPED_RECORD`'s `\d+` and render as *"You do not have access to this
   *    conversation."* — §6.3's named failure, a typo reading as a refusal.
   *  * **`fillPath` does not encode its parameters** (Backlog, demonstrated by `security-auditor`),
   *    and this is the one path parameter in the product that comes out of the address bar. A
   *    `../people/42` in it would be substituted verbatim and normalised by the transport into a
   *    request on **another route** — so the value is refused here, before anything is fetched,
   *    rather than trusted to a layer that does not encode it.
   *
   * ⚠️ **This component calls no hook that can make a request, and that is deliberate.** The session
   * read lives one level down (`Conversation`), so an id this gate refuses costs **zero** requests
   * rather than "no interview request" — `interview.test.tsx` asserts the empty list for `ada`,
   * `../people/42` and `1 OR 1`.
   */
  if (interviewId === undefined || !/^\d+$/.test(interviewId)) return <NotFound />;

  return <Conversation interviewId={Number(interviewId)} />;
}

/**
 * ⚠️ **No viewer, no query key.** Two readers legitimately receive different bodies for this one URL
 * (ADR 0006), so the key carries the viewer (`queryKeys.interview`) and a disabled query would still
 * read whatever sits at a placeholder key. The component that builds the key does not exist until
 * there is a viewer; until then this is §8's loading state, which is what it would render anyway.
 */
function Conversation({ interviewId }: { interviewId: number }) {
  const session = useSession();

  if (!session.data) return <InterviewSkeleton />;

  return <Answer viewerId={session.data.user.id} interviewId={interviewId} />;
}

function Answer({ viewerId, interviewId }: { viewerId: number; interviewId: number }) {
  const { t } = useTranslation('interviews');
  const interview = useQuery(interviewQuery(viewerId, interviewId));

  if (interview.isPending) return <InterviewSkeleton />;

  if (interview.isError) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, forbidden, throttled, or this
     * screen's own sentence. The `h1` and the way back stay — §8's states are not dead ends, and a
     * failed screen with no heading at all is the defect Sprint 8 found in a live browser.
     */
    return (
      <InterviewShell>
        <div className="mt-10">
          <ApiErrorState
            error={interview.error}
            message={t('error')}
            forbiddenMessage={t('forbidden')}
            onRetry={() => void interview.refetch()}
          />
        </div>
      </InterviewShell>
    );
  }

  const record = interview.data;

  /**
   * ⚠️ **The whole of protection #3 on this screen is this line.** Everything below the negative
   * branch holds `InterviewMetadataOut`, so `scores`, `overall_comment`, `overall_recommendation` and
   * `addenda` are a `tsc` error there rather than a rendering convention — the pins in
   * `api/interviews.test.tsx` fail the lint gate the day any of them stops being one.
   */
  if (!isReadable(record)) return <BlindedInterview interview={record} />;

  /**
   * §6.6 for the author of an unsubmitted interview, §6.7 for everything else — **both bodies are built
   * now** (TASK-082 and TASK-085). What TASK-080 owns is which of the two the address answers with, and
   * that branch is unchanged since: it reads `isReadable`, `submitted_at` and the author, and nothing
   * else. The *"everything else"* half is two payloads rather than one — a submitted interview, and the
   * impossible readable draft belonging to somebody else, which `Submitted` answers with a read-only page
   * and neither of §6.7's two sentences (see the note there).
   */
  if (record.submitted_at === null && record.interviewer_id === viewerId) {
    return (
      <InterviewShell>
        <FillingIn interview={record} viewerId={viewerId} />
      </InterviewShell>
    );
  }

  return (
    <InterviewShell>
      <Submitted interview={record} viewerId={viewerId} />
    </InterviewShell>
  );
}

/**
 * **The blinded state — designed by TASK-080, because neither §6.6 nor §6.7 has one** and it is the
 * answer this address gives the moment §6.5's rows link to it (TASK-087).
 *
 * ⚠️ **What it may say is bounded by the payload, and the payload is `InterviewMetadataOut`.** There
 * is **no candidate name, no vacancy title and no `person_id`** on it — that omission is deliberate on
 * the server (`InterviewMetadataOut`'s own docstring), because naming the candidate would tell somebody
 * who typed an id *which person* a hidden conversation is about. What it *does* carry is the
 * **interviewer's name** (ADR 0016, decision 2). So this state says *who* owes the write-up, says
 * *when*, says whether it is outstanding, says why the rest is not here, and stops.
 *
 * ⚠️ **This comment enumerated the payload's keys, and the enumeration was two sprints stale** — it
 * said five, `interviewer_name` had been on the shape since ADR 0016, and the bound it stated is what
 * kept the name off this screen while §6.5's card and §6.7's *Other interviews* both rendered it
 * (BUG-036). It names the **type** now and no count: the key set is `openapi.snapshot.json`'s, and
 * `api/interviews.test.tsx` asserts a blinded payload's keys equal it.
 *
 * ⚠️ **No rating, no comment, no verdict, no snapshot, no addendum — and no count of any of them.**
 * A count on a blinded row is not a lesser disclosure: *"4 answers, average withheld"* discloses
 * exactly the signal ADR 0008 exists to keep out of somebody else's judgement
 * (`backend/CONTEXT.md`, *Blinded*), and it would arrive from the client rather than from a
 * queryset, which is the direction §7 says this product loses invariants in. Nothing is filtered
 * here: if a field is on screen it was in the body, which is why the test pins the document's whole
 * text rather than the absence of a component.
 *
 * **What it is, in §8's terms: the Empty state.** The address resolved, the request was answered
 * `200`, the conversation exists — and there is nothing on it this reader may read. It is not an
 * error (nothing failed), not the Forbidden state (nothing was refused; a refusal on this surface is
 * a 404) and not a loading state (this *is* the answer). One sentence, and the way back is the
 * shell's link, on screen in every state, rather than a second copy of it inside this box.
 */
function BlindedInterview({ interview }: { interview: InterviewMetadataOut }) {
  const { t, i18n } = useTranslation('interviews');

  return (
    <InterviewShell>
      {/*
       * Who owes it, when it was, and whether it is written up. All three are facts about *that* a
       * conversation exists and is unanswered, which §6.2 makes the product's hero metric and is meant
       * to be visible; none of them says anything about its contents.
       *
       * ⚠️ **The name is rendered here by BUG-036, and the label is not decoration** (ADR 0016; §6.7's
       * *Other interviews* has named a blinded row since TASK-094 and this state did not). The `h1` is
       * the screen's own word *Interview* and this payload names **no candidate**, so a bare name under
       * that heading is read as the candidate of a hidden conversation — the one thing the shape omits
       * on purpose. *"Interviewer:"* is what keeps the line saying whose write-up is outstanding.
       */}
      <p className={cn(type.bodyPrimary, 'mt-1 font-medium')}>
        {t('blinded.interviewer', { name: interview.interviewer_name })}
      </p>
      <p className={cn(type.metadata, type.numeral, 'mt-1')}>
        <time dateTime={interview.scheduled_at}>
          {t('blinded.scheduled', { date: onDateTime(interview.scheduled_at, i18n.language) })}
        </time>
      </p>
      <p className={cn(type.body, 'mt-4')}>
        {interview.submitted_at === null
          ? t('blinded.outstanding')
          : t('blinded.submitted', { date: onDateTime(interview.submitted_at, i18n.language) })}
      </p>

      {/* §6.5's own sentence for the same fact on the same card, so the product says it once. */}
      <EmptyState className="mt-6" icon={EyeOff} message={t('blinded.hidden')} />
    </InterviewShell>
  );
}

/**
 * The screen's frame: §9's one `h1` and a way out, in **every** state.
 *
 * ⚠️ **The `h1` is the screen's own word and not the record**, which is the opposite of §6.5's choice
 * and forced rather than preferred: on the blinded answer there is **no candidate and no vacancy to name**
 * — the one name it carries belongs to the *interviewer*, and a record heading naming them would say the
 * conversation was with them — so a heading taken from the payload would be wrong where it existed and
 * missing in three of §8's five states besides. §6.9's bank made the same trade for
 * the milder version of the same reason, and it is what keeps this screen out of the four
 * `headingExceptions` the person card needs.
 *
 * ⚠️ **The way back is the candidate list and not the candidate.** `/candidates/:personId` is the
 * link a reader would want, and the blinded payload cannot build it — no `person_id` — so the one
 * destination every state can offer is the list, which §5 gives all three groups. A link that exists
 * in two states out of five is worse than one sentence that always works.
 */
function InterviewShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('interviews');

  return (
    /* §6.6: `max-w-3xl`. A conversation is read and written, not scanned across a wide monitor. */
    <div className="max-w-3xl">
      {/* A real `<a>` rather than a `navigate()` button, so the list opens in a new tab from here —
          and so the *"every rendered href resolves against buildRoutes()"* guard has something to
          walk (`personCard.test.tsx`'s device). */}
      <Link
        to="/candidates"
        className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('backToList')}
      </Link>

      <h1 className={cn(type.pageTitle, 'mt-2')}>{t('title')}</h1>

      {children}
    </div>
  );
}

/**
 * §8's loading state: skeletons in `paper-sunken` **shaped like the answer** — a line of metadata and
 * two blocks — never a centred spinner on a full page.
 *
 * The title is **not** a skeleton here, unlike §6.5's: it is the screen's own static word, so
 * announcing it costs nothing and replacing it with the same word when the payload lands is not the
 * layout jump §8 forbids. That is why this screen needs no `headingException` at all.
 */
function InterviewSkeleton() {
  const { t } = useTranslation('interviews');

  return (
    <InterviewShell>
      <div className="mt-4" aria-busy="true">
        <span className="sr-only">{t('loading')}</span>

        <Skeleton className="h-4 w-40" />
        <div className="mt-6 space-y-3 rounded border border-rule p-4">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <div className="mt-4 space-y-3 rounded border border-rule p-4">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    </InterviewShell>
  );
}
